// FCP design system — Modal primitive (v2.8.24)
//
// Modal/dialog with the modern-premium polish: backdrop blur, animated
// enter/exit, focus trap, Esc-to-close, body scroll lock, ARIA wiring,
// mobile bottom-sheet pattern below 480px.
//
// API:
//   <Modal
//     open={show}
//     onClose={() => setShow(false)}
//     title="Optional header title"
//     size="sm" | "md" (default) | "lg"
//     footer={<><Button>Cancel</Button><Button>Save</Button></>}
//     initialFocus={ref}        // optional, defaults to close button
//     closeOnOverlay={true}     // default true
//     closeOnEsc={true}         // default true
//     describedBy={...}         // optional aria-describedby ID
//   >
//     <p>Body content</p>
//   </Modal>
//
// If `title` is omitted, no header is rendered. Useful when the body
// itself contains the header treatment.
// If `footer` is omitted, no footer is rendered.

import React, { useEffect, useRef, useId, useCallback, useState } from 'react';
import { Icon } from './Icon.js';
import { Button } from './Button.js';
import { lockBodyScroll } from '../hooks/useBodyScrollLock.js';
import './Modal.css';

// Selector for focusable elements when computing the focus trap.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * useDelayedUnmount — keeps the modal mounted for `delay` ms after
 * `open` flips to false, so CSS exit animations have time to play.
 *
 * Returns { mounted, animationState } where animationState is one of:
 *   "entering" | "open" | "exiting" | "closed"
 *
 * The two "in flight" states are needed so CSS can target them with
 * different transitions.
 */
function useDelayedUnmount(open, delay = 180) {
  const [mounted, setMounted] = useState(open);
  const [animationState, setAnimationState] = useState(
    open ? 'open' : 'closed'
  );

  useEffect(() => {
    if (open) {
      setMounted(true);
      // Two-frame delay so the initial "entering" class paints first,
      // then "open" triggers the transition.
      requestAnimationFrame(() => {
        setAnimationState('entering');
        requestAnimationFrame(() => setAnimationState('open'));
      });
      return undefined;
    }

    // Closing: switch to exiting, schedule unmount after delay.
    setAnimationState('exiting');
    const timer = setTimeout(() => {
      setMounted(false);
      setAnimationState('closed');
    }, delay);
    return () => clearTimeout(timer);
  }, [open, delay]);

  return { mounted, animationState };
}

export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  footer,
  children,
  initialFocus,
  closeOnOverlay = true,
  closeOnEsc = true,
  describedBy,
  className = '',
  ...rest
}) {
  const titleId = useId();
  const surfaceRef = useRef(null);
  const lastFocusedRef = useRef(null);
  const { mounted, animationState } = useDelayedUnmount(open, 180);

  // Body scroll lock — only when actually open.
  //
  // Through the shared counted lock, NOT by saving and restoring the previous
  // value here. This used to do the latter, and the comment above it claimed a
  // modal count that the code did not implement. Both halves of that were
  // wrong in the same way: a dialog that writes back whatever it happened to
  // find can reinstate the mobile sidebar's lock after the sidebar has already
  // released it, leaving the page unscrollable with nobody holding a lock.
  // See client/src/ui/hooks/useBodyScrollLock.js.
  useEffect(() => {
    if (!open) return undefined;
    return lockBodyScroll();
  }, [open]);

  // Save the previously-focused element on open, restore on close.
  useEffect(() => {
    if (open) {
      lastFocusedRef.current = document.activeElement;
    } else if (lastFocusedRef.current && lastFocusedRef.current.focus) {
      // Defer one tick so the modal has fully unmounted.
      const el = lastFocusedRef.current;
      lastFocusedRef.current = null;
      setTimeout(() => {
        try { el.focus(); } catch (_) { /* element may have unmounted */ }
      }, 0);
    }
  }, [open]);

  // Initial focus when opening — either provided ref, the close button,
  // or the first focusable in the surface.
  useEffect(() => {
    if (animationState !== 'open' || !surfaceRef.current) return;

    const target =
      (initialFocus && initialFocus.current) ||
      surfaceRef.current.querySelector('[data-fcp-modal-close]') ||
      surfaceRef.current.querySelector(FOCUSABLE_SELECTOR);

    if (target && target.focus) {
      target.focus();
    }
  }, [animationState, initialFocus]);

  // Focus trap + Esc handler.
  const handleKeyDown = useCallback(
    (e) => {
      if (closeOnEsc && e.key === 'Escape') {
        e.stopPropagation();
        onClose && onClose();
        return;
      }

      if (e.key !== 'Tab' || !surfaceRef.current) return;

      const focusable = Array.from(
        surfaceRef.current.querySelectorAll(FOCUSABLE_SELECTOR)
      ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);

      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [closeOnEsc, onClose]
  );

  if (!mounted) return null;

  const handleOverlayClick = (e) => {
    if (!closeOnOverlay) return;
    // Only close when clicking the overlay itself, not the surface.
    if (e.target === e.currentTarget) {
      onClose && onClose();
    }
  };

  const surfaceClass = [
    'fcp-modal',
    `fcp-modal--${size}`,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={`fcp-modal-overlay fcp-modal-overlay--${animationState}`}
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="presentation"
    >
      <div
        ref={surfaceRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={describedBy}
        className={`${surfaceClass} fcp-modal--${animationState}`}
        {...rest}
      >
        {title && (
          <header className="fcp-modal__head">
            <h2 id={titleId} className="fcp-modal__title">{title}</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              aria-label="Close"
              className="fcp-modal__close"
              data-fcp-modal-close
            >
              <Icon name="close" size={18} />
            </Button>
          </header>
        )}

        <div className="fcp-modal__body">
          {children}
        </div>

        {footer && (
          <footer className="fcp-modal__footer">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

export default Modal;
